import { autoinject, transient, observable, computedFrom, useView } from "aurelia-framework";
import { GhgService } from "../../../../../../services/ghg/ghg.service";
import { ClassificationSocietyClientModel, EnergySavingEquipmentCategoryEnum, EnvironmentalVerificationSettings, GhgVerificationClientModel, GhgVerificationEnergySavingEquipmentModel, GhgVerificationStatusEnum, HullPreparationStandardClientModel, PaintType } from "../../../../../../models/ghgs/ghg.models";
import { NotificationType, NotificationClientService } from "../../../../../../services/basics/notification-client.service";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { Assignment } from "../../../../../components/assignment/assignment";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { LookupControl } from "../../../../../components/lookups/lookup-control/lookup-control";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { MenuItem } from "../../../../../components/menu/menu-item";
import { ValidationRules, ValidationController } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../../../client-validation-system/custom-validation-renderer";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { I18N } from "aurelia-i18n";
import { RightshipConfirmationDialogModel } from "../../../../../dialogs/rightship-confirmation-dialog/rightship-confirmation-dialog";
import { DialogServiceConstants } from "../../../../../../constants/basics/dialog.constants";
import { AttachmentsTypeGuids } from "../../../../../../constants/modules/attachments-type-guids";
import { RightshipEntityAttachmentManager } from "../../../common-steps/rightship-entity-attachment/rightship-entity-attachment-manager";
import { UserClaimsService } from "@rightship-tech/common-ui/services/basic/user-claims.service";
import { Claims } from "@rightship-tech/common-ui/constants/authentication/claims";

@autoinject()
@transient()
@useView('views/pages/modules/ghg/steps/ghg-energy-saving-equipment/ghg-energy-saving-equipment.html')
export class GhgEnergySavingEquipment {

    public ghgVerification: GhgVerificationClientModel;
    public energySavingEquipments: GhgVerificationEnergySavingEquipmentModel[] = [];
    public originalEnergySavingEquipments: GhgVerificationEnergySavingEquipmentModel[] = [];
    public someSelected: boolean;
    public assignment: Assignment;
    public customerId: number;
    public originalSnapshot: string;
    public seselookup: LookupControl[] = [];
    public reselookup: LookupControl[] = [];
    public rightshipEntityAttachmentManager: RightshipEntityAttachmentManager;
    public today: string = new Date().toJSON();
    public paintTypes: PaintType[];
    public yesNoCombobox = [{id: true, value: 'Yes'}, {id: false, value: 'No'}];
    public envVerificationSetting: EnvironmentalVerificationSettings;
    public classificationSocieties: ClassificationSocietyClientModel[] = [];
    public hullPreparationStandards: HullPreparationStandardClientModel[] = [];
    public submittedClassificationSocietyName: string;
    public submittedHullPreparationStandardName: string;
    private _ghgAdmin: boolean;    

    @computedFrom('ghgVerification.isClosed', 'customerId')
    public get canSave() {
        return this._ghgAdmin && this.ghgVerification && !this.ghgVerification.isClosed && !this.customerId;
    }

    @observable()
    selectAll: boolean;

    @computedFrom('rightshipEntityAttachmentManager.items', 'rightshipEntityAttachmentManager.items.length')
    public get documents() {
        const types = [
            AttachmentsTypeGuids.EnergySavingEquipment.toLowerCase()
        ]
        return this.rightshipEntityAttachmentManager.items.filter(x => types.some(y => x.attachmentTypeGuid && y == x.attachmentTypeGuid.toLowerCase()));
    }

    constructor(private ghgService: GhgService,
        private notificationClientService: NotificationClientService,
        private userClientService: UserClientService,
        private dialogClientService: DialogClientService,
        private validationController: ValidationController,
        private validationsClientService: ValidationsClientService,
        private i18n: I18N,
        private claimService: UserClaimsService) {
        this.customerId = userClientService.currentUser.settings.customerId;
        this.validationController.addRenderer(new CustomValidationRenderer());
        this._ghgAdmin = this.claimService.hasPermission(Claims.GhgAdmin);
    }

    public async activate(model: { ghgVerification: GhgVerificationClientModel, assignment: Assignment, rightshipEntityAttachmentManager: RightshipEntityAttachmentManager }) {

        if (!model.ghgVerification) {
            return;
        }

        this.ghgVerification = model.ghgVerification;
        this.assignment = model.assignment;
        this.rightshipEntityAttachmentManager = model.rightshipEntityAttachmentManager;
        const energySavingEquipments = await this.ghgService.getGhgVerificationEnergySavingEquipments(this.ghgVerification.ghgVerificationId);

        this.energySavingEquipments = energySavingEquipments;
            this.originalEnergySavingEquipments = CloneHelper.cloneObject(energySavingEquipments);
            this.originalSnapshot = JSON.stringify(energySavingEquipments.map(x => (
                {
                    passed: x.passed,
                    ghgRatingFactorSourceComment: x.ghgRatingFactorSourceComment,
                    ghgRatingFactorSourceId: x.ghgRatingFactorSourceId,
                    revisedEnergySavingEquipmentId: x.revisedEnergySavingEquipmentId,
                    revisedImplementedDate: x.revisedImplementedDate
                })));


        this.envVerificationSetting = await (this.ghgService.getEnvironmentalVerificationSettings());

        if (this.envVerificationSetting.ghgLowFrictionAntiFoulingSettings.enabled) {
            this.paintTypes = (await this.ghgService.getPaintTypes()).paintTypes;

            this.classificationSocieties = (await (this.ghgService.getClassificationSocieties())).classificationSocieties;
    
            this.hullPreparationStandards = (await (this.ghgService.getHullPreparationStandards())).hullPreparationStandards;
        }
    }

    getSubmitedClassificationSocietyName(id: number) {
        return this.classificationSocieties.find(x => x.classificationSocietyId == id)?.name;
    }

    getSubmitedHullPreparationStandardName(id: number) {
        return this.hullPreparationStandards.find(x => x.hullPreparationStandardId == id)?.name;
    }

    selectAllChanged() {
        this.energySavingEquipments.forEach(factor => {
            factor.isSelected = this.selectAll;
        });
        this.someSelected = this.energySavingEquipments.some(x => x.isSelected);
    }

    setSelectedAsPassed() {
        this.energySavingEquipments.forEach(factor => {
            if (factor.isSelected) {
                factor.passed = true;
            }
        });
        this.selectAll = false;
    }

    setSelectedAsFailed() {
        this.energySavingEquipments.forEach(factor => {
            if (factor.isSelected) {
                factor.passed = false;
            }
        });
        this.selectAll = false;
    }

    setSelectedAsNotApplicable() {
        this.energySavingEquipments.forEach(factor => {
            if (factor.isSelected) {
                factor.passed = null;
            }
        });
        this.selectAll = false;
    }

    onSelectedFactor() {
        this.someSelected = this.energySavingEquipments.some(x => x.isSelected);
    }

    hasChanges() {
        return this.canSave && this.originalSnapshot && this.originalSnapshot != JSON.stringify(this.energySavingEquipments.map(x => (
            {
                passed: x.passed,
                ghgRatingFactorSourceComment: x.ghgRatingFactorSourceComment,
                ghgRatingFactorSourceId: x.ghgRatingFactorSourceId,
                revisedEnergySavingEquipmentId: x.revisedEnergySavingEquipmentId,
                revisedImplementedDate: x.revisedImplementedDate
            })));
    }

    discardChanges() {
        this.energySavingEquipments = CloneHelper.cloneObject(this.originalEnergySavingEquipments);
    }

    save() {
        return new Promise<void>((resolve, reject) => {
            this.energySavingEquipments.forEach(equipment => {
                ValidationRules
                    .ensure<GhgVerificationEnergySavingEquipmentModel, number>(e => e.revisedEnergySavingEquipmentCategoryId)
                    .displayName('Category')
                    .required()
                    .when(e => !e.submittedEnergySavingEquipmentCategoryId)

                    .ensure<number>(e => e.revisedEnergySavingEquipmentId)
                    .displayName('Equipment')
                    .required()
                    .when(e => !e.submittedEnergySavingEquipmentId)

                    .ensure<string>(e => e.revisedImplementedDate)
                    .displayName('Date of certification')
                    .required()
                    .when(e => !e.submittedImplementedDate)
                    .on(equipment);
            });

            this.validationController.validate().then(response => {
                if (!this.isEquipmentValid() && !response.valid) {
                    reject();
                    return;
                }
                if (this.ghgVerification.assignedUserId == this.userClientService.currentUser.id) {
                    this.ghgService.saveGhgVerificationEnergySavingEquipments({
                        energySavingEquipments: this.energySavingEquipments,
                        ghgVerificationId: this.ghgVerification.ghgVerificationId
                    }).then(response => {

                        if (response.validationResult && response.validationResult.hasError) {
                            this.validationsClientService.showValidationErrors(response.validationResult);
                            reject();
                        } else {
                            this.energySavingEquipments = response.ghgVerificationEnergySavingEquipments;
                            this.notificationClientService.show("Energy saving equipment saved successfully", "", NotificationType.Success);
                            this.originalSnapshot = JSON.stringify(this.energySavingEquipments.map(x => (
                                {
                                    passed: x.passed,
                                    ghgRatingFactorSourceComment: x.ghgRatingFactorSourceComment,
                                    ghgRatingFactorSourceId: x.ghgRatingFactorSourceId,
                                    revisedEnergySavingEquipmentId: x.revisedEnergySavingEquipmentId,
                                    revisedImplementedDate: x.revisedImplementedDate
                                })));
                            this.originalEnergySavingEquipments = CloneHelper.cloneObject(this.energySavingEquipments);
                            resolve();
                        }
                    }).catch(() => {
                        this.notificationClientService.show("Failed to save Energy Saving Equipment", "", NotificationType.Error);
                        reject();
                    });
                } else {
                    this.dialogClientService.show(RightShipDialogServiceConstants.URLs.GhgVerificationAssignmentDialog, {
                        name: "Environmental Review Request",
                        submit: this.assignToMe,
                        callback: () => {
                            this.save().then(resolve).catch(reject);
                        },
                        onCancel: () => reject(),
                        currentAssignment: this.assignment.assignedEntity.assignedToUserName
                    }, true, false, false);
                }

            })
        });
    }

    assignToMe = () => {
        return new Promise<void>((resolve) => {
            if (this.assignment) {
                this.assignment.assignToMe().then(() => {
                    this.ghgVerification.ghgVerificationStatusId = GhgVerificationStatusEnum.InProgress;
                    this.ghgVerification.assignedUserId = this.userClientService.currentUser.id;
                    resolve();
                });
            } else {
                resolve();
            }
        });
    }

    onEquipmentSavingChanged(index: number) {
        const item = this.reselookup[index].selectedItem;
        if (item) {
            if (item.additionalData.showReductionToMainEnginePower && !item.additionalData.showMainAvailabilityFactor) {
                this.energySavingEquipments[index].revisedMainAvailabilityFactor = 1;
            }
            if (item.additionalData.showReductionToAuxiliaryEnginePower && !item.additionalData.showAuxiliaryAvailabilityFactor) {
                this.energySavingEquipments[index].revisedAuxiliaryAvailabilityFactor = 1;
            }
            if (!item.additionalData.showReductionToMainEnginePower) {
                this.energySavingEquipments[index].revisedReductionToMainEnginePower = 0;
                this.energySavingEquipments[index].revisedMainAvailabilityFactor = 0;
            }
            if (!item.additionalData.showReductionToAuxiliaryEnginePower) {
                this.energySavingEquipments[index].revisedReductionToAuxiliaryEnginePower = 0;
                this.energySavingEquipments[index].revisedAuxiliaryAvailabilityFactor = 0;
            }
            if (!item.additionalData.showMaker) {
                this.energySavingEquipments[index].revisedMaker = null;
            }
            if (!item.additionalData.showModel) {
                this.energySavingEquipments[index].revisedModel = null;
            }
            if (!item.additionalData.showStorageCapacity) {
                this.energySavingEquipments[index].revisedStorageCapacity = null;
            }
            if (!item.additionalData.showDescription) {
                this.energySavingEquipments[index].revisedDescription = null;
            }
            if (!item.additionalData.showCO2Reduction) {
                this.energySavingEquipments[index].revisedCO2Reduction = null;
            }
            if (!item.additionalData.showNumberOfUnits) {
                this.energySavingEquipments[index].revisedNumberOfUnits = null;
            }
            if (!item.additionalData.showEnergySavingAlternativeFuel) {
                this.energySavingEquipments[index].revisedEnergySavingAlternativeFuelId = null;
            }
        }
    }

    public getMenuItems(item: GhgVerificationClientModel): MenuItem[] {
        return [
            {
                click: () => this.showComments(item),
                isVisible: true,
                item: {
                    text: 'Comments'
                },
                classList: '',
            },
            {
                click: () => this.removeEquipment(item),
                isVisible: this.canBeRemoved(item),
                item: {
                    text: "Remove"
                },
                classList: ''
            }
        ];
    }

    showComments(item: GhgVerificationClientModel) {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.GhgEnergySavingEquipmentComments, {
            ghgEnergySavingEquipment: item,
            ghgVerification: this.ghgVerification
        }, true, false, false, 'right');
    }

    addEnergySavingEquipment() {
        this.energySavingEquipments.push({
            ghgRatingFactorSourceComment: null,
            ghgRatingFactorSourceId: null,
            ghgVerificationEnergySavingEquipmentId: 0,
            ghgVerificationId: this.ghgVerification.ghgVerificationId || 0,
            revisedEnergySavingEquipmentCategoryId: null,
            revisedEnergySavingEquipmentId: null,
            submittedEnergySavingEquipmentCategoryId: null,
            submittedEnergySavingEquipmentId: null,
            submittedEnergySavingEquipmentName: null,
            submittedEnergySavingEquipment: null,
            passed: null,
            revisedImplementedDate: null,
            submittedImplementedDate: null,
            isSelected: false,
            revisedAuxiliaryAvailabilityFactor: null,
            revisedMainAvailabilityFactor: null,
            revisedReductionToAuxiliaryEnginePower: null,
            revisedReductionToMainEnginePower: null,
            submittedAuxiliaryAvailabilityFactor: null,
            submittedMainAvailabilityFactor: null,
            submittedReductionToAuxiliaryEnginePower: null,
            submittedReductionToMainEnginePower: null,
            revisedCO2Reduction: null,
            revisedDescription: null,
            revisedEnergySavingAlternativeFuelId: null,
            revisedMaker: null,
            revisedModel: null,
            revisedNumberOfUnits: null,
            revisedStorageCapacity: null,
            submittedCO2Reduction: null,
            submittedDescription: null,
            submittedEnergySavingAlternativeFuelId: null,
            submittedMaker: null,
            submittedModel: null,
            submittedNumberOfUnits: null,
            submittedStorageCapacity: null,
            submittedClassificationSocietyId: null,
            revisedClassificationSocietyId: null,
            submittedPaintManufacturerId: null,
            revisedPaintManufacturerId: null,
            submittedPaintManufacturerName: null,
            revisedPaintManufacturerName: null,
            submittedPaintTypeId: null,
            revisedPaintTypeId: null,
            submittedProductName: null,
            revisedProductName: null,
            submittedIsZhip: null,
            revisedIsZhip: null,
            submittedApprovedZhipProduct: null,
            revisedApprovedZhipProduct: null
        });

        this.scrollToNewItem();
    }

    scrollToNewItem(wait: number = 500, maxRetry: number = 20) //retries every 1/2 second for 5 seconds
    {
        let count: number = 1;
        const tryScroll = () => {
            const items = $(".ghg-energy-saving-equipment-content").find(".comp-table--tbody").first().find(".comp-table--tbody-tr"); // checking if element for new item has been added yet
            if (items.length == this.energySavingEquipments.length) {
                const newEntry = items[items.length - 1]; // get the new element and scroll to it
                if (newEntry) {
                    newEntry.scrollIntoView({ behavior: "smooth" });
                }
            }
            else if (count < maxRetry) {
                count++;
                setTimeout(() => {
                    tryScroll();
                }, wait);
            }
        }

        tryScroll();
    }

    isEquipmentValid(): boolean {
        let isValid = true;
        for (const energySavingEquipment of this.energySavingEquipments) {
            const errorMessages = [];
            if (!energySavingEquipment.submittedEnergySavingEquipmentCategoryId
                && !energySavingEquipment.revisedEnergySavingEquipmentCategoryId) {
                isValid = false;
                errorMessages.push("Category is required.");
            }
            if (!energySavingEquipment.submittedEnergySavingEquipmentId
                && !energySavingEquipment.revisedEnergySavingEquipmentId) {
                isValid = false;
                errorMessages.push("Equipment is required.");
            }
            if (!energySavingEquipment.submittedImplementedDate
                && !energySavingEquipment.revisedImplementedDate) {
                isValid = false;
                errorMessages.push("Date of certification is required.");
            }

            if (!isValid && errorMessages.length > 0) {
                this.notificationClientService.show("Validation Error", errorMessages.join("<br>"), NotificationType.Error, 5000);
                return isValid;
            }
        }
        return isValid;
    }

    public canBeRemoved(item: any) {
        return item.ghgVerificationEnergySavingEquipmentId == 0
    }

    public removeEquipment = (item: any) => {
        return new Promise((resolve, reject) => {
            const model = new RightshipConfirmationDialogModel();
            model.confirm = this.i18n.tr('delete-text');
            model.cancel = this.i18n.tr('cancel-text');
            model.messages.push(this.i18n.tr('are-you-sure-you-want-to-delete'));
            model.title = this.i18n.tr('confirm-delete-text');
            model.onConfirm = () => this.deleteEnergySavingEquipment(item),
                model.onCancel = () => {
                    reject();
                },
                this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, true, false, false, 'right');
        });
    }

    public deleteEnergySavingEquipment(item: any) {
        if (item.ghgVerificationEnergySavingEquipmentId == 0) {
            const index = this.energySavingEquipments.indexOf(item);
            if (index > -1) {
                this.energySavingEquipments.splice(index, 1);
            }
        }
    }

    openEquationGraphicDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.EquationGraphicDialog, false, true, false, false);
    }

    updateOutcome(value: boolean, energySavingEquipment: GhgVerificationEnergySavingEquipmentModel) {
        if (this.ghgVerification.assignedUserId == this.userClientService.currentUser.id) {
            if (!this.canSave || !energySavingEquipment)
                return;

            if (energySavingEquipment) {
                energySavingEquipment.passed = value;
                
            }
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.GhgVerificationAssignmentDialog, {
                name: "Environmental Review Request",
                submit: this.assignToMe,
                callback: () => {
                    this.updateOutcome(value, energySavingEquipment);
                },
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    hasPaintTypes(index: number) {
        return this.energySavingEquipments[index].submittedEnergySavingEquipmentCategoryId == EnergySavingEquipmentCategoryEnum.Operations 
            && this.energySavingEquipments[index].submittedEnergySavingEquipment?.name?.toLowerCase().endsWith('hull coating');
    }
}
